import { useState } from 'react';
import { useTheme } from '../lib/theme-provider';
import { ThemedCard } from './ThemedCard';
import { ThemedButton } from './ThemedButton';
import { Sword, AlertCircle, X } from 'lucide-react';

interface Player {
  id: string;
  seatNumber: number;
  nickname: string;
  isEvil: boolean; // 刺客知道谁是邪恶阵营
}

interface AssassinationModalProps {
  isOpen: boolean;
  players: Player[];
  onClose: () => void;
  onConfirm: (targetSeat: number) => void;
}

export default function AssassinationModal({
  isOpen,
  players,
  onClose,
  onConfirm,
}: AssassinationModalProps) {
  const { theme } = useTheme();
  const [selectedSeat, setSelectedSeat] = useState<number | null>(null);

  if (!isOpen) return null;

  // 过滤出正义阵营玩家（刺客只能刺杀正义阵营）
  const goodPlayers = players.filter((p) => !p.isEvil);

  const handleConfirm = () => {
    if (selectedSeat === null) return;
    onConfirm(selectedSeat);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
    >
      <ThemedCard className="w-full max-w-md p-6">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <div
            className="flex items-center gap-2"
            style={{ color: '#f87171' }}
          >
            <Sword size={24} />
            <h2
              className="text-xl font-bold"
              style={{ color: '#f87171' }}
            >
              刺客刺杀
            </h2>
          </div>
        </div>

        {/* 提示 */}
        <div
          className="mb-6 p-3 rounded-lg text-center text-sm"
          style={{
            backgroundColor: `${theme.colors['accent-red']}20`,
            border: `1px solid ${theme.colors['accent-red']}40`,
            color: theme.colors['accent-red'],
          }}
        >
          刺杀成功则邪恶阵营直接获胜
        </div>

        {/* 玩家选择 */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {goodPlayers.map((player) => (
            <button
              key={player.id}
              onClick={() => setSelectedSeat(player.seatNumber)}
              className="flex flex-col items-center p-3 rounded-xl transition-all"
              style={{
                backgroundColor:
                  selectedSeat === player.seatNumber
                    ? `${theme.colors['accent-gold']}30`
                    : theme.colors['bg-primary'],
                border: `2px solid ${
                  selectedSeat === player.seatNumber
                    ? theme.colors['accent-gold']
                    : `${theme.colors['border-secondary']}40`
                }`,
              }}
            >
              {/* 头像 */}
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold mb-2"
                style={{
                  backgroundColor: theme.colors['accent-purple'],
                  color: theme.colors['bg-primary'],
                }}
              >
                {player.nickname[0]}
              </div>
              {/* 昵称 */}
              <span
                className="text-xs font-medium truncate w-full text-center"
                style={{ color: theme.colors['text-primary'] }}
              >
                {player.nickname}
              </span>
              {/* 座位号 */}
              <span
                className="text-xs"
                style={{ color: theme.colors['text-muted'] }}
              >
                #{player.seatNumber}
              </span>

              {/* 已选择标记 */}
              {selectedSeat === player.seatNumber && (
                <div
                  className="absolute -top-1 -right-1 px-2 py-0.5 rounded-full text-xs font-bold"
                  style={{
                    backgroundColor: theme.colors['accent-gold'],
                    color: theme.colors['bg-primary'],
                  }}
                >
                  已选择
                </div>
              )}
            </button>
          ))}
        </div>

        {/* 当前选择提示 */}
        {selectedSeat !== null && (
          <div
            className="mb-4 p-3 rounded-lg flex items-center gap-2"
            style={{
              backgroundColor: `${theme.colors['accent-gold']}10`,
              border: `1px solid ${theme.colors['accent-gold']}30`,
            }}
          >
            <AlertCircle size={16} style={{ color: theme.colors['accent-gold'] }} />
            <span
              className="text-sm font-medium"
              style={{ color: theme.colors['accent-gold'] }}
            >
              当前刺杀目标选择：#{selectedSeat}{' '}
              {goodPlayers.find((p) => p.seatNumber === selectedSeat)?.nickname}
            </span>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex gap-3">
          <ThemedButton variant="secondary" onClick={onClose} className="flex-1">
            <X size={18} className="mr-1" />
            取消
          </ThemedButton>
          <ThemedButton
            onClick={handleConfirm}
            disabled={selectedSeat === null}
            className="flex-1"
            style={{
              backgroundColor: selectedSeat !== null ? theme.colors['accent-red'] : undefined,
            }}
          >
            <Sword size={18} className="mr-1" />
            确认刺杀
          </ThemedButton>
        </div>
      </ThemedCard>
    </div>
  );
}
